import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { usePraxis } from "../context/PraxisContext";

export default function Header() {
  const { services: LEISTUNGEN } = usePraxis();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileLeist, setMobileLeist] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const dropRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Mobile-Menü + Dropdown bei Routenwechsel schließen
  useEffect(() => {
    setMobileOpen(false);
    setMobileLeist(false);
    setDropOpen(false);
  }, [location.pathname, location.hash]);

  // Dropdown per Klick außerhalb schließen
  useEffect(() => {
    const onDocClick = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) {
        setDropOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Springt zu #leistungen – auch wenn wir schon dort sind
  const goLeistungen = useCallback(
    (e) => {
      e.preventDefault();
      setDropOpen(false);
      setMobileOpen(false);
      setMobileLeist(false);

      const doScroll = () => {
        const el = document.getElementById("leistungen");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      };

      if (location.pathname === "/") {
        doScroll();
        // Hash im URL setzen, ohne History-Eintrag
        if (window.history.replaceState) {
          window.history.replaceState(null, "", "/#leistungen");
        }
      } else {
        navigate("/#leistungen");
      }
    },
    [location.pathname, navigate]
  );

  // Springt zu #kontakt
  const goKontakt = useCallback(
    (e) => {
      e.preventDefault();
      setMobileOpen(false);
      if (location.pathname === "/") {
        const el = document.getElementById("kontakt");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        navigate("/#kontakt");
      }
    },
    [location.pathname, navigate]
  );

  return (
    <header className="site-header" data-testid="site-header">
      <div className="site-header-inner">
        <Link to="/" className="brand" data-testid="brand-link">
          <span className="brand-name">Dr. med. Inka Scheer</span>
          <span className="brand-sub">Gynäkologie · Bürstadt</span>
        </Link>

        <nav className="nav-desktop" aria-label="Hauptnavigation">
          <div
            className={`nav-dropdown${dropOpen ? " open" : ""}`}
            data-testid="nav-leistungen"
            ref={dropRef}
          >
            <button
              className="nav-dropdown-toggle"
              aria-haspopup="true"
              aria-expanded={dropOpen}
              onClick={() => setDropOpen((v) => !v)}
            >
              Leistungen <ChevronDown size={14} />
            </button>
            <div className="nav-dropdown-menu" role="menu">
              {LEISTUNGEN.map((l) => (
                <a
                  key={l.slug}
                  href="/#leistungen"
                  onClick={goLeistungen}
                  className="nav-dropdown-item"
                  data-testid={`nav-leistung-${l.slug}`}
                >
                  {l.title}
                </a>
              ))}
            </div>
          </div>

          <a
            href="/#kontakt"
            onClick={goKontakt}
            className="btn btn-gold"
            data-testid="nav-termin-btn"
          >
            Termin vereinbaren
          </a>
        </nav>

        <button
          className="menu-toggle"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Menü"
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <div className={`mobile-menu ${mobileOpen ? "open" : ""}`}>
        <button
          onClick={() => setMobileLeist(!mobileLeist)}
          data-testid="mobile-leistungen-toggle"
        >
          Leistungen <ChevronDown size={14} style={{ marginLeft: "auto" }} />
        </button>
        <div className={`mobile-submenu ${mobileLeist ? "open" : ""}`}>
          {LEISTUNGEN.map((l) => (
            <a
              key={l.slug}
              href="/#leistungen"
              onClick={goLeistungen}
              data-testid={`mobile-leistung-${l.slug}`}
            >
              {l.title}
            </a>
          ))}
        </div>
        <a href="/#kontakt" onClick={goKontakt} data-testid="mobile-termin-link">
          Termin vereinbaren
        </a>
      </div>
    </header>
  );
}
